import {Divider} from '@genestack/ui';
import React from 'react';

import {LocalErrorBoundary} from '../../../../components/error-boundary';
import {Sidebar} from '../../../../components/layout/sidebar';

import {LayoutContextValue} from '../../../../components/layout/page-layout/use-page-layout';
import {useAddRecordToExploreViewSearchHistoryMutation} from '../hooks/requests/use-add-record-to-explore-view-search-history-mutation';
import {
    DOC_NAME_SEARCH_PREFIX,
    useSuggestionsProps
} from '../../common/sidebar-search-field/hooks/use-suggestions-props';
import {useExploreViewSearchHistoryQuery} from '../hooks/requests/use-explore-view-search-history-query';
import {SearchData} from '../../common/sidebar-search-field/interface';
import {SearchResultComponentProps} from './search-results/interface';
import {useCategoriesFilters} from '../hooks/use-categories-filters';
import {useSearchSidebarLoadingState} from '../hooks/use-search-sidebar-loading-state';
import {useHandleFindUsages} from '../../common/sidebar-search-field/hooks/use-handle-find-usages';
import {SidebarHeader} from '../../../../components/layout/sidebar/sidebar-header/sidebar-header';
import {ExploreDocumentsSidebarProps} from './interface';
import {NodeResults} from './search-results/node-results/node-results';
import {SearchHistory} from './search-results/search-history';
import {DocResults} from './search-results/doc-results';
import {CategoryResults} from './search-results/category-results';
import {SidebarSearchField} from '../../common/sidebar-search-field/sidebar-search-field';
import {useSidebarSearchProps} from '../../common/sidebar-search-field/hooks/use-sidebar-search-props';
import {
    isSearchByDocName,
    isSearchCategory,
    isSearchNode
} from '../../common/sidebar-search-field/suggestion-helpers';

interface Props extends ExploreDocumentsSidebarProps {
    layoutSettings: LayoutContextValue;
    searchSidebarProps: ReturnType<typeof useSidebarSearchProps>;
    findUsagesProps: ReturnType<typeof useHandleFindUsages>;
    revisionId?: string;
}

export const SearchDocumentsSidebar = (props: Props) => {
    const {
        layoutSettings,
        searchSidebarProps,
        findUsagesProps,
        selectedGlobalVersion,
        getDocumentLink,
        footer,
        closeSidebar,
        documentId,
        revisionId
    } = props;
    const addItemToSearchHistoryMutation = useAddRecordToExploreViewSearchHistoryMutation();
    const {isExpandedSidebar, toggleIsExpandedSidebar} = layoutSettings;
    const {loadingState, setLoadingState} = useSearchSidebarLoadingState();

    const categoriesFilters = useCategoriesFilters();

    const searchHistory = useExploreViewSearchHistoryQuery(
        !searchSidebarProps.inputPrefix && !searchSidebarProps.inputValue
    );

    const suggestionsProps = useSuggestionsProps({
        currentGlobalVersion: selectedGlobalVersion,
        inputValue: searchSidebarProps.inputValue,
        inputPrefix: searchSidebarProps.inputPrefix,
        searchHistory: searchHistory.data
    });

    const onSearchFieldBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        if (
            searchSidebarProps.inputPrefix === DOC_NAME_SEARCH_PREFIX &&
            searchSidebarProps.inputValue &&
            e.target instanceof HTMLInputElement
        ) {
            addItemToSearchHistoryMutation.mutate({
                categoriesSearch: null,
                referencedNode: null,
                stringSearch: {strict: false, value: searchSidebarProps.inputValue}
            });
        }
    };

    const header = (
        <SidebarHeader title="Search" onClose={closeSidebar}>
            <Divider variant="transparent" gap={1} />
            <SidebarSearchField
                searchSidebarProps={searchSidebarProps}
                disabled={
                    Boolean(revisionId) ||
                    loadingState.isLoadingNextPage ||
                    findUsagesProps.loadingNodeType
                }
                busy={
                    suggestionsProps.isLoading ||
                    searchHistory.isLoading ||
                    findUsagesProps.loadingNodeType
                }
                onBlur={onSearchFieldBlur}
                suggestionsProps={suggestionsProps}
            />
        </SidebarHeader>
    );

    return (
        <Sidebar component="div" header={header} footer={footer} border="right">
            <LocalErrorBoundary>
                {(function () {
                    const resultsProps: Omit<
                        SearchResultComponentProps<SearchData>,
                        'searchData'
                    > = {
                        versionId: selectedGlobalVersion.id,
                        expanded: isExpandedSidebar,
                        onToggleExpand: toggleIsExpandedSidebar,
                        categoriesFilters,
                        loadingState,
                        setLoadingState,
                        selectedGlobalVersion,
                        getDocumentLink,
                        selectedDocumentId: documentId
                    };

                    if (!searchSidebarProps.searchData) {
                        return <SearchHistory search={searchSidebarProps.search} />;
                    }

                    if (isSearchByDocName(searchSidebarProps.searchData)) {
                        return (
                            <DocResults
                                {...resultsProps}
                                searchData={searchSidebarProps.searchData}
                            />
                        );
                    }

                    if (isSearchNode(searchSidebarProps.searchData)) {
                        return (
                            <NodeResults
                                {...resultsProps}
                                searchData={searchSidebarProps.searchData}
                            />
                        );
                    }

                    if (isSearchCategory(searchSidebarProps.searchData)) {
                        return (
                            <CategoryResults
                                {...resultsProps}
                                searchData={searchSidebarProps.searchData}
                            />
                        );
                    }

                    return null;
                })()}
            </LocalErrorBoundary>
        </Sidebar>
    );
};
